import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as visasApi from './visas.api';

import type {
  CreateVisaPricingTierRequest,
  CreateVisaRequest,
  UpdateVisaPricingTierRequest,
  UpdateVisaRequest,
  VisaApprovalRequest,
  VisaListQuery,
} from '@utazzo/shared';

const QK = {
  list: (query: VisaListQuery) => ['visas', 'list', query] as const,
  detail: (id: string) => ['visas', 'detail', id] as const,
  tiers: (visaId: string) => ['visas', 'tiers', visaId] as const,
};

export const useVisasList = (query: VisaListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => visasApi.listVisas(query),
    staleTime: 30_000,
  });

export const useVisaDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => visasApi.getVisa(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

export const useCreateVisa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVisaRequest) => visasApi.createVisa(payload),
    onSuccess: (visa) => {
      qc.invalidateQueries({ queryKey: ['visas', 'list'] });
      toast.success(`Added ${visa.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add visa'),
  });
};

export const useUpdateVisa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateVisaRequest }) =>
      visasApi.updateVisa(id, payload),
    onSuccess: (visa) => {
      qc.invalidateQueries({ queryKey: ['visas', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(visa.id) });
      toast.success('Visa updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update visa'),
  });
};

export const useApproveVisa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: VisaApprovalRequest }) =>
      visasApi.approveVisa(id, payload),
    onSuccess: (visa, vars) => {
      qc.invalidateQueries({ queryKey: ['visas', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(visa.id) });
      toast.success(
        `${vars.payload.tier === 'admin' ? 'Admin' : 'Moderator'} ${vars.payload.approve ? 'approved' : 'approval revoked'}`,
      );
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to change approval'),
  });
};

export const useDeleteVisa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => visasApi.deleteVisa(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visas', 'list'] });
      toast.success('Visa deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete visa'),
  });
};

export const useBulkDeleteVisas = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => visasApi.bulkDeleteVisas(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visas'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete visas'),
  });
};

// ---------- Pricing tier hooks ----------

export const useVisaPricingTiers = (visaId: string | null | undefined) =>
  useQuery({
    queryKey: QK.tiers(visaId ?? ''),
    queryFn: () => visasApi.listVisaPricingTiers(visaId as string),
    staleTime: 30_000,
    enabled: Boolean(visaId),
  });

export const useCreateVisaPricingTier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ visaId, payload }: { visaId: string; payload: CreateVisaPricingTierRequest }) =>
      visasApi.createVisaPricingTier(visaId, payload),
    onSuccess: (tier, vars) => {
      qc.invalidateQueries({ queryKey: QK.tiers(vars.visaId) });
      qc.invalidateQueries({ queryKey: QK.detail(vars.visaId) });
      toast.success(`Added tier ${tier.tierName}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add tier'),
  });
};

export const useUpdateVisaPricingTier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      visaId,
      tierId,
      payload,
    }: {
      visaId: string;
      tierId: string;
      payload: UpdateVisaPricingTierRequest;
    }) => visasApi.updateVisaPricingTier(visaId, tierId, payload),
    onSuccess: (_tier, vars) => {
      qc.invalidateQueries({ queryKey: QK.tiers(vars.visaId) });
      qc.invalidateQueries({ queryKey: QK.detail(vars.visaId) });
      toast.success('Tier updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update tier'),
  });
};

export const useDeleteVisaPricingTier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ visaId, tierId }: { visaId: string; tierId: string }) =>
      visasApi.deleteVisaPricingTier(visaId, tierId),
    onSuccess: (_v, vars) => {
      qc.invalidateQueries({ queryKey: QK.tiers(vars.visaId) });
      qc.invalidateQueries({ queryKey: QK.detail(vars.visaId) });
      toast.success('Tier deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete tier'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
